import React, { useEffect, useState } from "react";
import { api, inr } from "../lib/api";
import { toast } from "sonner";
import { Wallet, IndianRupee, ArrowDownToLine, Loader2, X } from "lucide-react";

export default function DesignerWallet() {
  const [data, setData] = useState(null);
  const [showRequest, setShowRequest] = useState(false);

  const load = () => api.get("/designer/wallet").then(r => setData(r.data));
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  if (!data) return <div className="p-8 text-center text-gray-500">Loading…</div>;
  const { balance, ledger, payouts } = data;

  return (
    <div className="space-y-4" data-testid="designer-wallet">
      <h2 className="text-2xl font-bold text-[#1a3c5e] inline-flex items-center gap-2"><Wallet className="w-6 h-6 text-[#F7941D]" /> My Wallet</h2>

      <div className="grid sm:grid-cols-4 gap-3">
        <BalCard label="Earned" value={balance.earned} testid="dw-earned" tone="emerald" />
        <BalCard label="Paid Out" value={balance.paid_out} testid="dw-paid" tone="gray" />
        <BalCard label="Pending Payouts" value={balance.pending_payout} testid="dw-pending" tone="amber" />
        <BalCard label="Available to Withdraw" value={balance.available} testid="dw-available" tone="orange" big />
      </div>

      <div className="flex justify-end">
        <button onClick={() => setShowRequest(true)} data-testid="dw-request-btn" disabled={balance.available <= 0}
          className="px-4 py-2 bg-[#F7941D] hover:bg-[#e0861a] disabled:opacity-40 text-white text-sm font-bold rounded-xl inline-flex items-center gap-1">
          <ArrowDownToLine className="w-4 h-4" /> Request Payout
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Recent Activity">
          {ledger.length === 0 ? <Empty /> : ledger.map(e => (
            <div key={e.id} className="flex items-center justify-between px-2 py-2 border-b border-gray-100 text-sm" data-testid={`dw-ledger-${e.id}`}>
              <div>
                <div className="text-xs uppercase font-bold" style={{ color: e.type === "credit" ? "#059669" : "#dc2626" }}>{e.type}</div>
                <div className="text-xs text-gray-500">{e.description}</div>
              </div>
              <div className={`font-bold ${e.type === "credit" ? "text-emerald-700" : "text-rose-700"}`}>{e.type === "credit" ? "+" : "-"} {inr(e.amount)}</div>
            </div>
          ))}
        </Section>
        <Section title="Payout Requests">
          {payouts.length === 0 ? <Empty /> : payouts.map(p => (
            <div key={p.id} className="px-2 py-2 border-b border-gray-100 text-sm" data-testid={`dw-payout-${p.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold">{inr(p.amount)} via {p.method}</span>
                <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${p.status === "paid" ? "bg-emerald-100 text-emerald-700" : p.status === "rejected" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{p.status}</span>
              </div>
              <div className="text-xs text-gray-500">{(p.created_at || "").slice(0, 10)} · {p.upi_id || "—"}</div>
            </div>
          ))}
        </Section>
      </div>

      {showRequest && <RequestModal available={balance.available} onClose={() => setShowRequest(false)} onDone={() => { setShowRequest(false); load(); }} />}
    </div>
  );
}

function BalCard({ label, value, testid, tone, big }) {
  const tones = { emerald: "bg-emerald-50 text-emerald-700 border-emerald-200", gray: "bg-gray-50 text-gray-700 border-gray-200", amber: "bg-amber-50 text-amber-700 border-amber-200", orange: "bg-orange-50 text-orange-700 border-orange-200" };
  return <div className={`p-4 rounded-2xl border ${tones[tone]} ${big ? "ring-2 ring-[#F7941D]" : ""}`} data-testid={testid}><div className="text-xs uppercase font-semibold opacity-70">{label}</div><div className={`font-bold mt-1 ${big ? "text-2xl" : "text-xl"}`}>{inr(value)}</div></div>;
}
function Section({ title, children }) { return <div className="bg-white rounded-2xl border border-gray-200 p-4"><h3 className="text-sm font-bold uppercase text-[#1a3c5e] mb-2">{title}</h3>{children}</div>; }
function Empty() { return <div className="text-center text-sm text-gray-500 py-6">No data.</div>; }

function RequestModal({ available, onClose, onDone }) {
  const [amount, setAmount] = useState(available);
  const [method, setMethod] = useState("UPI");
  const [upi, setUpi] = useState("");
  const [bankAccount, setBankAccount] = useState("");
  const [bankIfsc, setBankIfsc] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (Number(amount) <= 0 || Number(amount) > available) { toast.error("Invalid amount"); return; }
    setSaving(true);
    try {
      await api.post("/designer/wallet/payouts", {
        amount: Number(amount), method,
        upi_id: method === "UPI" ? upi : null,
        bank_details: method === "BANK" ? { account_number: bankAccount, ifsc: bankIfsc } : null,
        notes,
      });
      toast.success("Payout requested");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full">
        <div className="border-b border-gray-200 p-4 flex items-center justify-between"><h3 className="font-bold text-[#1a3c5e]">Request Payout</h3><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-4 space-y-3">
          <div className="text-xs text-gray-500">Available: <strong className="text-emerald-700">{inr(available)}</strong></div>
          <label><div className="text-xs text-gray-600 mb-1">Amount</div><input data-testid="dw-amount" type="number" value={amount} onChange={e => setAmount(e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" /></label>
          <label><div className="text-xs text-gray-600 mb-1">Method</div>
            <select value={method} onChange={e => setMethod(e.target.value)} data-testid="dw-method" className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"><option value="UPI">UPI</option><option value="BANK">Bank Transfer</option></select></label>
          {method === "UPI" && <label><div className="text-xs text-gray-600 mb-1">UPI ID</div><input data-testid="dw-upi" value={upi} onChange={e => setUpi(e.target.value)} placeholder="yourname@upi" className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" /></label>}
          {method === "BANK" && <>
            <label><div className="text-xs text-gray-600 mb-1">Account Number</div><input data-testid="dw-acc" value={bankAccount} onChange={e => setBankAccount(e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" /></label>
            <label><div className="text-xs text-gray-600 mb-1">IFSC</div><input data-testid="dw-ifsc" value={bankIfsc} onChange={e => setBankIfsc(e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" /></label>
          </>}
          <label><div className="text-xs text-gray-600 mb-1">Notes</div><textarea data-testid="dw-notes" value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" /></label>
        </div>
        <div className="border-t border-gray-200 p-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl hover:bg-gray-100">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="dw-submit" className="px-5 py-2 bg-[#F7941D] hover:bg-[#e0861a] text-white text-sm font-bold rounded-xl inline-flex items-center gap-1">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Request</button>
        </div>
      </div>
    </div>
  );
}
